import { useState } from 'react';
import { Modal, Button, Select, Typography, Space, message, Spin } from 'antd';
import { EnvironmentOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useLocationStore } from '@/stores/location.store';
import { apiService } from '@/services/api.service';
import { City, ReverseGeocodeResponse } from '@rescue-foodies/shared-types';

const { Title, Text } = Typography;

interface FirstTimeLocationModalProps {
  open: boolean;
  onComplete: () => void;
}

/**
 * FirstTimeLocationModal component
 * Prompts first-time users to set their location
 */
export const FirstTimeLocationModal = ({ open, onComplete }: FirstTimeLocationModalProps) => {
  const { setLocation, setHasPromptedUser, setLoading } = useLocationStore();
  const [selectedCityName, setSelectedCityName] = useState<string | undefined>();
  const [isDetecting, setIsDetecting] = useState(false);

  // Fetch supported cities from API
  const { data: cities, isLoading: citiesLoading } = useQuery<City[]>({
    queryKey: ['locations', 'cities'],
    queryFn: () => apiService.get<City[]>('/locations/cities'),
    enabled: open, // Only fetch when modal is open
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  /**
   * Handle "Use My Location" button click
   */
  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      message.error('Geolocation is not supported by your browser');
      return;
    }

    setIsDetecting(true);
    setLoading(true);

    try {
      // Get user's current position
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: false,
          maximumAge: 300000,
        });
      });

      const { latitude, longitude } = position.coords;

      // Call reverse geocode API
      const result = await apiService.get<ReverseGeocodeResponse>(
        '/locations/reverse-geocode',
        {
          params: { lat: latitude, lng: longitude },
        }
      );

      setLocation(result.city, result.coordinates.lat, result.coordinates.lng);
      setHasPromptedUser(true);
      message.success(`Location set to ${result.city}`);
      onComplete();
    } catch (error: any) {
      console.error('Geolocation error:', error);

      // Handle specific geolocation errors
      if (error.code === 1) {
        message.error('Location access denied. Please select your city manually');
      } else if (error.code === 2) {
        message.error('Location unavailable. Please select your city manually');
      } else if (error.code === 3) {
        message.error('Location request timed out. Please select your city manually');
      } else {
        message.error('Could not detect your location. Please select manually');
      }
    } finally {
      setIsDetecting(false);
      setLoading(false);
    }
  };

  /**
   * Handle manual city selection and modal close
   */
  const handleManualSelection = () => {
    if (!selectedCityName) {
      message.warning('Please select a city');
      return;
    }

    const city = Array.isArray(cities) ? cities.find((c) => c.name === selectedCityName) : undefined;
    if (city) {
      setLocation(city.name, city.lat, city.lng);
      setHasPromptedUser(true);
      message.success(`Location set to ${city.name}`);
      onComplete();
    }
  };

  /**
   * Handle skip button - set default to Quezon City
   */
  const handleSkip = () => {
    const defaultCity = Array.isArray(cities) ? (cities.find((c) => c.name === 'Quezon City') || cities[0]) : undefined;
    if (defaultCity) {
      setLocation(defaultCity.name, defaultCity.lat, defaultCity.lng);
      setHasPromptedUser(true);
      message.info(`Defaulting to ${defaultCity.name}`);
      onComplete();
    }
  };

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={480}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
          <EnvironmentOutlined style={{ fontSize: 48, color: '#F5A623' }} />
          <Title level={3} style={{ margin: 0 }}>
            Where are you looking for food?
          </Title>
          <Text type="secondary">
            We'll show you available surprise bags near you
          </Text>
        </Space>

        {/* Use My Location button (Primary action) */}
        <Button
          type="primary"
          size="large"
          block
          icon={isDetecting ? <LoadingOutlined /> : <EnvironmentOutlined />}
          onClick={handleUseMyLocation}
          loading={isDetecting}
          disabled={isDetecting}
        >
          {isDetecting ? 'Detecting location...' : 'Use My Location'}
        </Button>

        {/* Divider text */}
        <div style={{ textAlign: 'center', color: '#999' }}>
          <Text type="secondary">or</Text>
        </div>

        {/* Manual city selection */}
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Text strong>Select Manually:</Text>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              size="large"
              style={{ width: '100%' }}
              placeholder="Choose your city"
              value={selectedCityName}
              onChange={setSelectedCityName}
              loading={citiesLoading}
              disabled={isDetecting || citiesLoading}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={Array.isArray(cities) ? cities.map((city) => ({
                value: city.name,
                label: city.name,
              })) : []}
            />
            <Button
              size="large"
              type="default"
              onClick={handleManualSelection}
              disabled={!selectedCityName || isDetecting || citiesLoading}
            >
              Confirm
            </Button>
          </Space.Compact>
        </Space>

        {/* Skip button */}
        <Button
          type="link"
          block
          onClick={handleSkip}
          disabled={isDetecting || citiesLoading}
          style={{ marginTop: 8 }}
        >
          Skip (Default to Quezon City)
        </Button>
      </Space>
    </Modal>
  );
};
